import { JSX, ReactNode, useEffect, useState } from 'react';
import {
  ColumnType,
  OnRowClickType,
  PaginationFooter,
  Table,
} from 'src/components/molecules';
import { PaginatedTableContainer } from './PaginatedTable.styles';

interface Props<Data> {
  columns: ColumnType[];
  sortableColumns?: string[];
  columnInfoTooltips?: { columnLabel: string; tooltip: string }[];
  tableData: { cellData: (ReactNode | string | number)[]; row: string }[];
  allowRowSelect?: boolean;
  selectedRows?: string[];
  onRowClick?: OnRowClickType<Data>;
  numPages?: number;
  initialPage?: number;
  handleClickPage?: (index: number) => void;
  maxRows?: number;
  rowHeight?: number;
  showPagination?: boolean;
  onColumnSort?: (columnName: string, direction: 'asc' | 'desc') => void;
  sortedColumn?: { columnName: string; direction: 'asc' | 'desc' };
  'data-testid'?: string;
  fixedRowHeight?: boolean;
  isScrollable?: boolean;
  minHeight?: number;
  height?: string;
  minWidth?: number;
  isFullWidth?: boolean;
  isFullHeight?: boolean;
  buttons?: {
    left: { text: string; onClick: () => void };
    right: { text: string; onClick: () => void };
  };
  borderRadius?: string;
  paginationPadding?: string;
  visibleColumns: ColumnType[];
  customEmptyState?: JSX.Element;
  currentPage?: number;
  isAlreadyPaginated?: boolean; // set to true if pagination is being handled by the backend
  emptyText: string;
  selectAll?: () => void;
  allSelected?: boolean;
  maxColumnWidth?: string;
  columnsWidths?: (string | undefined)[];
  emptyStateLayout?: 'default' | 'borderless';
  /** Rounded container that scrolls horizontally (required for sticky last column). */
  horizontalScrollContainer?: boolean;
}

export const PaginatedTable = <Data,>({
  columns,
  tableData,
  maxRows,
  rowHeight,
  isScrollable = false,
  initialPage = 1,
  borderRadius,
  isAlreadyPaginated = false,
  isFullHeight = false,
  ...props
}: Props<Data>) => {
  const [rowsVisible, setRowsVisible] = useState([
    (initialPage - 1) * (maxRows ?? tableData.length),
    initialPage * (maxRows ?? tableData.length),
  ]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const handlePageClick = (index: number) => {
    if (props.handleClickPage) {
      props.handleClickPage(index);
    }
    if (!isAlreadyPaginated) {
      if (maxRows && tableData.length > maxRows) {
        setRowsVisible([(index - 1) * maxRows, index * maxRows]);
      }
      setCurrentPage(index);
    }
  };

  useEffect(() => {
    setRowsVisible([
      (initialPage - 1) * (maxRows ?? tableData.length),
      initialPage * (maxRows ?? tableData.length),
    ]);
  }, [initialPage, maxRows, tableData.length]);

  const handleColumnSort = (columnName: string, direction: 'asc' | 'desc') => {
    props.onColumnSort?.(columnName, direction);
    handlePageClick(1);
  };
  const containerDimensions = document
    ?.getElementById(`${props['data-testid']}-container`)
    ?.getBoundingClientRect();

  return (
    <PaginatedTableContainer
      data-testid={`${props['data-testid']}-container`}
      id={`${props['data-testid']}-container`}
      minHeight={props.minHeight ?? containerDimensions?.height}
      height={props.height}
      minWidth={props.minWidth}
      $borderRadius={borderRadius}
      $horizontalScroll={props.horizontalScrollContainer}
    >
      <Table
        columns={columns}
        tableData={
          isAlreadyPaginated
            ? tableData
            : tableData.slice(rowsVisible[0], rowsVisible[1])
        }
        allowRowSelect={props.allowRowSelect}
        selectedRows={props.selectedRows}
        onRowClick={props.onRowClick}
        onColumnSort={handleColumnSort}
        sortableColumns={props.sortableColumns}
        customEmptyState={props.customEmptyState}
        sortedColumn={props.sortedColumn}
        data-testid={props['data-testid']}
        fixedRowHeight={props.fixedRowHeight}
        isScrollable={
          props.minWidth && containerDimensions?.width
            ? containerDimensions?.width === props.minWidth
            : isScrollable
        }
        isFullWidth={props.isFullWidth}
        isFullHeight={isFullHeight}
        rowHeight={rowHeight}
        maxRows={maxRows}
        borderRadius={borderRadius}
        visibleColumns={props.visibleColumns}
        emptyText={props.emptyText}
        selectAll={props.selectAll}
        allSelected={props.allSelected}
        columnInfoTooltips={props.columnInfoTooltips}
        maxColumnWidth={props.maxColumnWidth}
        columnsWidths={props.columnsWidths}
        emptyStateLayout={props.emptyStateLayout}
      />
      <PaginationFooter
        numPages={
          props.numPages ??
          (maxRows ? Math.ceil(tableData.length / maxRows) : 0)
        }
        initialPage={initialPage}
        handlePageClick={handlePageClick}
        showPagination={Boolean(
          tableData.length !== 0 &&
          (props.showPagination || (maxRows && tableData.length > maxRows)),
        )}
        buttons={props.buttons}
        paginationPadding={props.paginationPadding}
        currentPage={props.currentPage ?? currentPage}
        data-testid={`${props['data-testid']}-pagination`}
      />
    </PaginatedTableContainer>
  );
};
